import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { paletteAt, heightT } from "./config.js";

/**
 * Ornaments: the floating models from the old site (skull, drone, M4,
 * teddy, glue gun, plane) reborn as particle clouds hanging from the
 * tips of branches. Each is sampled to a point cloud so it shares the
 * bioluminescent language of the rest of the scene.
 */
const MODELS = [
  { url: "/assets/models/skull.obj", scale: 1.0 },
  { url: "/assets/models/MQ-9-b.obj", scale: 1.35 },
  { url: "/assets/models/m4.obj", scale: 1.1 },
  { url: "/assets/models/teddy.obj", scale: 1.0 },
  { url: "/assets/models/gluegun.obj", scale: 1.0 },
  { url: "/assets/models/plane.obj", scale: 1.15 }
];

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;
  uniform vec3 uColor;

  attribute float aSeed;

  varying vec3 vColor;
  varying float vTwinkle;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    float twinkle = 0.6 + 0.4 * sin(uTime * 1.9 + aSeed * 39.0);
    gl_PointSize = 1.1 * twinkle * uPixelRatio * (120.0 / -mv.z);
    gl_PointSize = min(gl_PointSize, 5.0 * uPixelRatio);
    vColor = uColor;
    vTwinkle = twinkle;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vColor;
  varying float vTwinkle;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float disc = smoothstep(0.5, 0.06, d);
    gl_FragColor = vec4(vColor, disc * disc * vTwinkle * 0.36);
  }
`;

/** Collect every vertex position in the OBJ into one normalized cloud. */
function toPointCloud(obj, maxPoints) {
  const chunks = [];
  let total = 0;
  obj.traverse((child) => {
    if (!child.isMesh) return;
    const attr = child.geometry?.getAttribute("position");
    if (!attr) return;
    chunks.push(attr);
    total += attr.count;
  });
  if (!total) return null;

  const stride = Math.max(1, Math.floor(total / maxPoints));
  const picked = [];
  for (const attr of chunks) {
    for (let i = 0; i < attr.count; i += stride) {
      picked.push(attr.getX(i), attr.getY(i), attr.getZ(i));
    }
  }

  const positions = new Float32Array(picked);
  const count = positions.length / 3;

  // Center and normalize to a unit-ish cloud.
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    v.fromArray(positions, i * 3);
    box.expandByPoint(v);
  }
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const scale = 1 / (Math.max(size.x, size.y, size.z) || 1);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (positions[i * 3] - center.x) * scale;
    positions[i * 3 + 1] = (positions[i * 3 + 1] - center.y) * scale;
    positions[i * 3 + 2] = (positions[i * 3 + 2] - center.z) * scale;
  }

  const seeds = new Float32Array(count);
  for (let i = 0; i < count; i++) seeds[i] = Math.random();

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  return geo;
}

/** Pick spaced-out tips, keeping clear of the artworks. */
function pickSpots(tips, avoid, count) {
  const spots = [];
  const shuffled = [...tips].sort(() => Math.random() - 0.5);
  for (const tip of shuffled) {
    if (spots.length >= count) break;
    let ok = true;
    for (const a of avoid) {
      if (tip.distanceTo(a) < 3.2) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    for (const s of spots) {
      if (tip.distanceTo(s) < 4.5) {
        ok = false;
        break;
      }
    }
    if (ok) spots.push(tip);
  }
  return spots;
}

/**
 * @param {Array<THREE.Vector3>} tips branch tip positions from buildTree
 * @param {Array<THREE.Vector3>} avoid artwork positions to keep clear of
 */
export function buildOrnaments({ tips, avoid, tier }) {
  const group = new THREE.Group();
  const instances = [];

  const maxOrnaments = tier.name === "mobile" ? 6 : 12;
  const pointsPerModel = tier.name === "mobile" ? 900 : 2200;
  const spots = pickSpots(tips, avoid, maxOrnaments);

  const loader = new OBJLoader();

  spots.forEach((spot, i) => {
    const spec = MODELS[i % MODELS.length];
    loader.load(
      spec.url,
      (obj) => {
        const geo = toPointCloud(obj, pointsPerModel);
        if (!geo) return;

        const [r, g, b] = paletteAt(heightT(spot.y));
        const mat = new THREE.ShaderMaterial({
          vertexShader: VERT,
          fragmentShader: FRAG,
          uniforms: {
            uTime: { value: 0 },
            uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
            uColor: {
              // Slightly lifted toward white to stand apart from branches,
              // but kept below the bloom threshold so silhouettes stay crisp.
              value: new THREE.Color(
                r * 0.85 + 0.1,
                g * 0.85 + 0.1,
                b * 0.85 + 0.1
              )
            }
          },
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending
        });

        const points = new THREE.Points(geo, mat);
        points.position.copy(spot);
        points.scale.setScalar(2.7 * spec.scale);
        points.rotation.set(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          0
        );
        points.frustumCulled = false;
        group.add(points);

        instances.push({
          points,
          mat,
          baseY: spot.y,
          spinSpeed: 0.1 + Math.random() * 0.12,
          spinAxis: new THREE.Vector3(
            Math.random() - 0.5,
            1,
            Math.random() - 0.5
          ).normalize(),
          bobPhase: Math.random() * Math.PI * 2,
          bobAmp: 0.1 + Math.random() * 0.1
        });
      },
      undefined,
      () => {} // a missing model just means one fewer ornament
    );
  });

  const spinQ = new THREE.Quaternion();

  function update(t, dt) {
    for (const inst of instances) {
      inst.mat.uniforms.uTime.value = t;
      spinQ.setFromAxisAngle(inst.spinAxis, inst.spinSpeed * dt);
      inst.points.quaternion.premultiply(spinQ);
      inst.points.position.y =
        inst.baseY + Math.sin(t * 0.4 + inst.bobPhase) * inst.bobAmp;
    }
  }

  return { group, update };
}
